import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ResponsiveNavBar } from "../../common/ResponsiveNavBar";
import { useNavOffset } from "../../common/navLayout";
import { getSupportBaseUrl } from "../../utils/supportUrl";
import { Dispatcher } from "../WireGuardTunnels/wireGuardTunnelsInterfaces";
import { MULISH } from "../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import {
  Card,
  CardHeader,
  EmptyCard,
  Hint,
  Mono,
  Pill,
  SectionLabel,
  SiteLink,
  StatusDot,
  TS_COLORS,
  TsButton,
} from "../Troubleshooting/TroubleshootingUi";
import {
  formatDateTime,
  formatRelative,
  pluralize,
} from "../Troubleshooting/TroubleshootingUtils/formatters";
import { copyToClipboard } from "../Troubleshooting/TroubleshootingUtils/buildDiagnosticReport";
import { useTroubleshootingStore } from "../Troubleshooting/troubleshootingStore/troubleshootingStore";
import { fetchVpnStatus, VpnStatusRow } from "./vpnStatusApi";
import {
  affectedSites,
  downVpns,
  KIND_LABEL,
  lastSeen,
  showsGroups,
  statusLabel,
  statusTone,
} from "./vpnStatusUtils";

/**
 * VPN status page (redesign, ENG-2531).
 *
 * Read-only: every WireGuard tunnel and IPsec site-to-site VPN of the user's
 * accounts, with its status, last handshake / status change, the ranges routed
 * over it and the sites that use it. Routes are managed by us (overlaps between
 * customers have to be checked against our route table), so there are no add
 * or edit controls — the page says how to ask for a new one instead.
 */
export const VpnStatus = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const nav = useNavOffset(navBarCollapsed);
  const navigate = useNavigate();
  const [vpns, setVpns] = React.useState<VpnStatusRow[] | null>(null);
  const [checkedAt, setCheckedAt] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [toast, setToast] = React.useState<string | null>(null);
  const [, setTick] = React.useState(0);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchVpnStatus();
      setVpns(data.vpns);
      setCheckedAt(data.checked_at);
    } catch {
      setError("Could not load your VPNs. Try again in a minute.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Keep "Checked N min ago" honest without refetching.
  React.useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 15_000);
    return () => window.clearInterval(id);
  }, []);

  const copy = async (text: string, what: string) => {
    const ok = await copyToClipboard(text);
    setToast(ok ? `Copied ${what}.` : "Could not copy to the clipboard.");
  };

  const openTroubleshooter = (vpn: VpnStatusRow) => {
    const store = useTroubleshootingStore.getState();
    if (vpn.kind === "wireguard") {
      store.openTabForSite("wireguard", null);
      store.setWgSubTab("debugger");
    } else {
      store.openTabForSite("connections", null);
    }
    navigate("/troubleshooter");
  };

  const down = vpns ? downVpns(vpns) : [];
  const affected = vpns ? affectedSites(vpns) : [];
  const withGroups = vpns ? showsGroups(vpns) : false;

  return (
    <div>
      <Snackbar
        open={toast != null}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      <div
        className="absolute inset-0 overflow-auto bg-actuate-grey"
        style={{ top: nav.top }}
      >
        <Box sx={{ ml: `${nav.left}px`, p: { xs: 2, md: 3 }, maxWidth: 1200 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Typography
              component="h1"
              sx={{
                ...MULISH,
                fontSize: 24,
                fontWeight: 800,
                color: TS_COLORS.navy,
              }}
            >
              VPN
            </Typography>
            <Hint sx={{ flex: "1 1 260px" }}>
              Your WireGuard tunnels and IPsec site-to-site VPNs: whether each
              is up, the networks routed over it, and the sites that use it.
            </Hint>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {checkedAt && <Hint>Checked {formatRelative(checkedAt)}</Hint>}
              <TsButton
                variant="outline"
                size="sm"
                onClick={() => void load()}
                disabled={loading}
                startIcon={<RefreshIcon fontSize="small" />}
              >
                Refresh
              </TsButton>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ ...MULISH, mb: 2 }}>
              {error}
            </Alert>
          )}

          {down.length > 0 && (
            <Alert
              severity="error"
              data-testid="vpn-down-callout"
              sx={{ ...MULISH, mb: 2 }}
              action={
                <TsButton size="sm" onClick={() => openTroubleshooter(down[0])}>
                  Open the Troubleshooter
                </TsButton>
              }
            >
              <strong>
                {down.length === 1
                  ? `${down[0].name} is down.`
                  : `${down.length} VPNs are down: ${down
                      .map((v) => v.name)
                      .join(", ")}.`}
              </strong>{" "}
              Cameras behind {down.length === 1 ? "it" : "them"} cannot be
              reached until it is back up.
              {affected.length > 0 && (
                <>
                  {" "}
                  Affected sites:{" "}
                  {affected.map((site, index) => (
                    <React.Fragment key={site.id}>
                      {index > 0 && ", "}
                      <SiteLink siteId={site.id}>{site.name}</SiteLink>
                    </React.Fragment>
                  ))}
                  .
                </>
              )}
            </Alert>
          )}

          {loading && vpns == null ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress aria-label="Loading VPNs" />
            </Box>
          ) : vpns && vpns.length === 0 ? (
            <EmptyCard>
              You have no WireGuard tunnels or site-to-site VPNs with us.
            </EmptyCard>
          ) : (
            <Box sx={{ display: "grid", gap: 2 }}>
              {(vpns ?? []).map((vpn) => (
                <VpnCard
                  key={vpn.id}
                  vpn={vpn}
                  showGroup={withGroups}
                  onCopy={copy}
                  onTroubleshoot={() => openTroubleshooter(vpn)}
                />
              ))}
            </Box>
          )}

          <Box
            data-testid="vpn-contact-note"
            sx={{
              ...MULISH,
              mt: 3,
              p: 2,
              borderRadius: "6px",
              backgroundColor: TS_COLORS.blueTint,
              color: TS_COLORS.blue,
              fontSize: 14,
            }}
          >
            <strong>Need another network routed?</strong> Routes are managed by
            Actuate, because every new range has to be checked against the
            ranges already routed for other networks.{" "}
            <Box
              component="a"
              href={getSupportBaseUrl()}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: TS_COLORS.blue, fontWeight: 800 }}
            >
              Contact us
            </Box>{" "}
            with the network and the site it is for.
          </Box>
        </Box>
      </div>
      <div className="justify-start">
        <ResponsiveNavBar
          navBarCollapsed={navBarCollapsed}
          setNavBarCollapsed={setNavBarCollapsed}
        />
      </div>
    </div>
  );
};

const VpnCard = ({
  vpn,
  showGroup,
  onCopy,
  onTroubleshoot,
}: {
  vpn: VpnStatusRow;
  showGroup: boolean;
  onCopy: (text: string, what: string) => void;
  onTroubleshoot: () => void;
}) => {
  const seen = lastSeen(vpn);
  const accent =
    vpn.status === "down"
      ? TS_COLORS.red
      : vpn.kind === "wireguard"
      ? TS_COLORS.wg
      : TS_COLORS.blue;
  return (
    <Card accent={accent} testId={`vpn-card-${vpn.id}`}>
      <CardHeader
        title={vpn.name}
        leading={
          <Pill tone={vpn.kind === "wireguard" ? "wg" : "info"}>
            {KIND_LABEL[vpn.kind]}
          </Pill>
        }
        hint={showGroup && vpn.group ? vpn.group.name : undefined}
        right={
          <>
            <StatusDot tone={statusTone(vpn)} label={statusLabel(vpn)} />
            {vpn.status === "down" && (
              <TsButton size="sm" variant="outline" onClick={onTroubleshoot}>
                Troubleshoot
              </TsButton>
            )}
          </>
        }
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "220px 1fr 1fr" },
          gap: 2.5,
          px: 2.5,
          py: 2,
        }}
      >
        <Box>
          <SectionLabel>{seen.label}</SectionLabel>
          <Typography
            sx={{ ...MULISH, fontSize: 14 }}
            title={seen.iso ? formatDateTime(seen.iso) : undefined}
          >
            {seen.iso ? formatRelative(seen.iso) : "Not reported"}
          </Typography>
          {vpn.tunnel_ip && (
            <Box sx={{ mt: 1.5 }}>
              <SectionLabel>Tunnel address</SectionLabel>
              <Mono>{vpn.tunnel_ip}</Mono>
            </Box>
          )}
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SectionLabel>Routed networks</SectionLabel>
            {vpn.routes.length > 1 && (
              <Tooltip title="Copy all">
                <IconButton
                  size="small"
                  aria-label={`Copy all routes for ${vpn.name}`}
                  onClick={() => onCopy(vpn.routes.join("\n"), "all routes")}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {vpn.routes.length === 0 ? (
            <Hint>
              {vpn.sites == null
                ? "Could not read the routes right now."
                : "No networks routed."}
            </Hint>
          ) : (
            <Box
              component="ul"
              sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 0.5 }}
            >
              {vpn.routes.map((cidr) => (
                <Box
                  component="li"
                  key={cidr}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Mono>{cidr}</Mono>
                  <Tooltip title="Copy">
                    <IconButton
                      size="small"
                      aria-label={`Copy ${cidr}`}
                      onClick={() => onCopy(cidr, cidr)}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
          {vpn.routes_virtual && vpn.routes.length > 0 && (
            <Hint sx={{ display: "block", mt: 0.5 }}>
              Translated on your side (virtually assigned).
            </Hint>
          )}
        </Box>

        <Box>
          <SectionLabel>Sites using it</SectionLabel>
          {vpn.sites == null ? (
            <Hint>Unknown until the routes can be read.</Hint>
          ) : vpn.sites.length === 0 ? (
            <Hint>No sites found behind these networks.</Hint>
          ) : (
            <>
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  display: "grid",
                  gap: 0.5,
                }}
              >
                {vpn.sites.map((site) => (
                  <Box
                    component="li"
                    key={site.id}
                    sx={{ ...MULISH, fontSize: 14 }}
                  >
                    <SiteLink siteId={site.id}>{site.name}</SiteLink>
                  </Box>
                ))}
              </Box>
              {vpn.camera_count != null && (
                <Hint sx={{ display: "block", mt: 0.5 }}>
                  {pluralize(vpn.camera_count, "camera")} at{" "}
                  {pluralize(vpn.sites.length, "site")}
                </Hint>
              )}
            </>
          )}
        </Box>
      </Box>
    </Card>
  );
};
